/**
 * Stops browser translation extensions from crashing the app.
 *
 * THE BUG (reproduced 2026-09-06). Ray reported "出问题了" partway through a challenge on
 * learn.novara.tech/learn. Forcing Chrome to translate the page and answering the first
 * question reproduces it every time; the same click in English does not. The thrown error is
 *
 *     NotFoundError: Failed to execute 'removeChild' on 'Node':
 *     The node to be removed is not a child of this node.
 *
 * WHY IT HAPPENS. A translator rewrites text in place: it takes a text node, wraps it in a
 * <font> element, and puts the translation there. React still holds a reference to the ORIGINAL
 * text node and still believes it is a direct child of the element it rendered it into. It is
 * not — it now sits one level deeper, inside the <font>. The next render that removes or
 * reorders that text calls removeChild/insertBefore with a node that is no longer the caller's
 * child, the DOM throws, React unmounts the tree, and the error boundary shows its card.
 *
 * Answering a question is exactly such a render: the Submit button goes, the hint block goes,
 * the feedback card arrives. That is why it fires there and not on load.
 *
 * WHY THIS IS OURS TO FIX. Novara is English-only and will stay that way (LANGUAGE_SWITCHER is
 * off). Our two closest evaluators — Ray in Hong Kong, Mr. Chen in California — are both native
 * Chinese speakers, and so were the friends of Mr. Chen's who trialled the product. Translating
 * an English-only app is the predictable thing for those users to do, and "the extension did it"
 * is not a defence when the result is a learner losing a challenge mid-answer.
 *
 * WHAT THIS DOES. Makes the two DOM calls React relies on tolerant of a node that has been
 * reparented underneath it: removing something already detached becomes a no-op, and inserting
 * before a missing sibling appends instead. React's model and the visible DOM can then disagree
 * about position, so a translated string may go stale until the next full re-render. That is a
 * cosmetic defect. The alternative is the whole page dying, so it is the right trade.
 *
 * WHAT THIS DOES NOT DO. It does not make translation correct, and it is a safety net rather
 * than a repair — the underlying JSX still emits several text nodes where one would be safe
 * (`Checkpoint {i} of {n}`). Those sites are the real fix and are tracked separately; there are
 * 19 of them across 15 files, which is precisely why the net goes in first.
 *
 * THE OBJECTION, AND THE ANSWER. Patching Node.prototype can hide a genuine React bug by
 * swallowing a removeChild that SHOULD have thrown. So every interception is counted and the
 * first one warns, with whether the page looked translated at the time. A masked bug shows up
 * as interceptions on an untranslated page instead of disappearing silently. Read the counters
 * from the console at any time:
 *
 *     window.__novaraTranslationGuard
 */

// Google Translate marks the document; other extensions do not, so this is a hint and not a
// test. It exists to tell "we absorbed a translator's damage" apart from "we absorbed one of
// our own bugs" when reading the counters back.
function looksTranslated() {
  try {
    const html = document.documentElement;
    return (
      html.classList.contains('translated-ltr') ||
      html.classList.contains('translated-rtl') ||
      document.querySelector('font[_msttexthash], font[style*="vertical-align"]') != null
    );
  } catch (e) {
    return false;
  }
}

export const stats = {
  removeChild: 0,
  insertBefore: 0,
  whileTranslated: 0,
  whileNotTranslated: 0,
};

let uninstall = null;

/**
 * Wrap the two DOM methods. Returns a function that puts the originals back, or null if the
 * guard was already installed or there is no DOM. Nothing in the app calls the disposer — it
 * exists so tests can restore the prototype instead of leaking a patch across the suite.
 */
export function installTranslationCrashGuard() {
  // Idempotent: a second install would wrap the already-wrapped methods and double-count.
  if (uninstall || typeof Node === 'undefined' || !Node.prototype) return null;

  const record = (kind) => {
    stats[kind] += 1;
    if (looksTranslated()) stats.whileTranslated += 1;
    else stats.whileNotTranslated += 1;
    if (stats.removeChild + stats.insertBefore === 1) {
      // Once per session. This is a handled condition, not an error — warn so it is visible in
      // a support screenshot without looking like the crash it replaced.
      // eslint-disable-next-line no-console
      console.warn(
        '[novara] A DOM node was moved by something outside React (usually a page translator). ' +
          'Absorbed to avoid crashing; some text may not update until the next screen. ' +
          'Details: window.__novaraTranslationGuard'
      );
    }
  };

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild(child) {
    if (child && child.parentNode !== this) {
      record('removeChild');
      return child; // already gone, or reparented under a <font> — nothing to do
    }
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      record('insertBefore');
      return this.appendChild(newNode); // right element, possibly wrong position
    }
    return originalInsertBefore.apply(this, arguments);
  };

  if (typeof window !== 'undefined') window.__novaraTranslationGuard = stats;

  uninstall = () => {
    Node.prototype.removeChild = originalRemoveChild;
    Node.prototype.insertBefore = originalInsertBefore;
    uninstall = null;
    stats.removeChild = 0;
    stats.insertBefore = 0;
    stats.whileTranslated = 0;
    stats.whileNotTranslated = 0;
  };
  return uninstall;
}
